import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useMemo,
} from 'react';
import { Annotation } from './types';

export type EditDialogFocusTarget = 'note' | null;

export interface AnnotationDialogsContextValue {
  // Dialog state management
  isEditDialogOpen: boolean;
  isDeleteDialogOpen: boolean;
  selectedAnnotation: Annotation | null;

  // Actions
  openEditDialog: (
    annotation: Annotation,
    focusTarget?: EditDialogFocusTarget,
  ) => void;
  openDeleteDialog: (annotation: Annotation) => void;
  closeEditDialog: () => void;
  closeDeleteDialog: () => void;

  // Focus target
  editDialogFocusTarget: EditDialogFocusTarget;
}

export const AnnotationDialogsContext = createContext<
  AnnotationDialogsContextValue | undefined
>(undefined);

interface AnnotationDialogsProviderProps {
  children: ReactNode;
  onAnnotationDeleted?: (annotation: Annotation) => void;
}

export default function AnnotationDialogsProvider({
  children,
}: Readonly<AnnotationDialogsProviderProps>) {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedAnnotation, setSelectedAnnotation] =
    useState<Annotation | null>(null);
  const [editDialogFocusTarget, setEditDialogFocusTarget] =
    useState<EditDialogFocusTarget>(null);

  const openEditDialog = useCallback(
    (annotation: Annotation, focusTarget?: EditDialogFocusTarget) => {
      setSelectedAnnotation(annotation);
      setEditDialogFocusTarget(focusTarget ?? null);
      setIsEditDialogOpen(true);
    },
    [],
  );

  const openDeleteDialog = useCallback((annotation: Annotation) => {
    setSelectedAnnotation(annotation);
    setIsDeleteDialogOpen(true);
  }, []);

  const closeEditDialog = useCallback(() => {
    setIsEditDialogOpen(false);
    setSelectedAnnotation(null);
    setEditDialogFocusTarget(null);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setSelectedAnnotation(null);
  }, []);

  const contextValue = useMemo(
    () => ({
      isEditDialogOpen,
      editDialogFocusTarget,
      isDeleteDialogOpen,
      selectedAnnotation,
      openEditDialog,
      openDeleteDialog,
      closeEditDialog,
      closeDeleteDialog,
    }),
    [
      isEditDialogOpen,
      editDialogFocusTarget,
      isDeleteDialogOpen,
      selectedAnnotation,
      openEditDialog,
      openDeleteDialog,
      closeEditDialog,
      closeDeleteDialog,
    ],
  );

  return (
    <AnnotationDialogsContext.Provider value={contextValue}>
      {children}
    </AnnotationDialogsContext.Provider>
  );
}

export function useAnnotationDialogsContext(): AnnotationDialogsContextValue {
  const context = useContext(AnnotationDialogsContext);
  if (!context) {
    throw new Error(
      'useAnnotationDialogsContext must be used within an AnnotationDialogsProvider',
    );
  }
  return context;
}
